import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { EVENT_LABELS, isViaCopilot } from "../lib/activity";
import ViaCopilotBadge from "./ViaCopilotBadge";
import { roleBadgeClass } from "../lib/roles";
import { getInitials, avatarColorClass } from "../lib/avatar";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "Inconnue");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Inconnue");

function Row({ label, children }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800 break-words">{children}</dd>
    </>
  );
}

// Users page detail drawer (replaces the « Voir le profil » dialog). The server decides what each block shows: work counts only for
// a reader allowed to see everyone's prospects/tasks, recent actions only with the activity log, the collaborator fiche only for an
// administrator. `actions` are the same buttons as the row's « … » menu, already filtered by the page.
export default function UserDetailSheet({ userRow, onClose, actions = null }) {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  useEffect(() => {
    if (!userRow) return undefined;
    let alive = true;
    setState({ status: "loading", data: null, error: "" });
    api.get(`/users/${userRow.id}/overview`)
      .then(({ data }) => { if (alive) setState({ status: "ready", data, error: "" }); })
      .catch((e) => { if (alive) setState({ status: "error", data: null, error: errorMessage(e, "Chargement impossible") }); });
    return () => { alive = false; };
  }, [userRow]);

  const u = state.data || userRow;
  const active = u?.is_active !== false;
  const work = state.data?.work || {};
  const activity = state.data?.recent_activity;

  return (
    <Sheet open={!!userRow} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="user-detail">
        {u && (
          <>
            <SheetHeader>
              <div className="flex items-center gap-3 pr-6">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${avatarColorClass(u.id)}`}>
                  {getInitials(u.name, u.email)}
                </div>
                <div className="min-w-0 text-left">
                  <SheetTitle className="truncate">{u.name}</SheetTitle>
                  <SheetDescription className="truncate">{u.email}</SheetDescription>
                </div>
              </div>
            </SheetHeader>
            <div className="space-y-5 py-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`border-transparent text-2xs font-medium ${roleBadgeClass(u.role)}`}>{state.data?.role_label || u.role}</Badge>
                <span className={`inline-flex items-center gap-1.5 text-sm ${active ? "text-emerald-700" : "text-red-600"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-red-500"}`} />{active ? "Actif" : "Désactivé"}
                </span>
                {state.data?.must_change_password && (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-2xs font-medium text-amber-900">Doit changer son mot de passe</span>
                )}
              </div>

              {state.status === "loading" && <Skeleton className="h-40 w-full" />}
              {state.status === "error" && <p className="text-sm text-red-700">{state.error}</p>}

              {state.data && (
                <>
                  <section className="space-y-2">
                    <h3 className="text-xs font-semibold text-slate-500">Compte</h3>
                    <dl className="grid grid-cols-[10rem_minmax(0,1fr)] gap-y-2 text-sm">
                      <Row label="Dernière connexion">{state.data.last_login ? fmtDateTime(state.data.last_login) : "Jamais connecté"}</Row>
                      <Row label="Créé le">{fmtDate(state.data.created_at)}</Row>
                      <Row label="WhatsApp">{state.data.whatsapp_number || "Non renseigné"}</Row>
                      <Row label="Alertes WhatsApp">{state.data.notify_whatsapp ? "Activées" : "Désactivées"}</Row>
                      {"collaborator" in state.data && (
                        <Row label="Fiche collaborateur">
                          {state.data.collaborator ? (
                            <Link to={`/collaborateurs/${state.data.collaborator.id}`} className="text-jamora hover:underline">
                              {state.data.collaborator.reference}{state.data.collaborator.job_title ? ` · ${state.data.collaborator.job_title}` : ""}
                            </Link>
                          ) : "Aucune"}
                        </Row>
                      )}
                    </dl>
                  </section>

                  {Object.keys(work).length > 0 && (
                    <section className="space-y-2" data-testid="user-detail-work">
                      <h3 className="text-xs font-semibold text-slate-500">Travail en cours</h3>
                      <div className="grid grid-cols-3 gap-2">
                        {"prospects" in work && <Stat label="Prospects assignés" value={work.prospects} />}
                        {"open_tasks" in work && <Stat label="Tâches ouvertes" value={work.open_tasks} />}
                        {"overdue_tasks" in work && <Stat label="En retard" value={work.overdue_tasks} warn={work.overdue_tasks > 0} />}
                      </div>
                    </section>
                  )}

                  {activity && (
                    <section className="space-y-2" data-testid="user-detail-activity">
                      <h3 className="text-xs font-semibold text-slate-500">Dernières actions</h3>
                      {activity.length === 0 ? <p className="text-sm text-slate-500">Aucune action enregistrée.</p> : (
                        <ul className="divide-y divide-slate-100 rounded-md border border-slate-100">
                          {activity.map((e) => (
                            <li key={e.id || `${e.event_type}-${e.occurred_at}`} className="px-3 py-2 text-sm">
                              <div className="flex items-center justify-between gap-2">
                                <span className={`truncate ${e.outcome && e.outcome !== "success" ? "text-red-700" : "text-slate-800"}`}>{EVENT_LABELS[e.event_type] || e.event_type}</span>
                                <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(e.occurred_at)}</span>
                              </div>
                              {(e.resource_label || isViaCopilot(e)) && (
                                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                  {e.resource_label && <span className="truncate">{e.resource_label}</span>}
                                  {isViaCopilot(e) && <ViaCopilotBadge />}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  )}
                </>
              )}

              {actions && <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">{actions}</div>}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Stat({ label, value, warn = false }) {
  return (
    <div className={`rounded-lg border p-3 ${warn ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}>
      <div className={`text-lg font-semibold tabular-nums ${warn ? "text-amber-900" : "text-slate-900"}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
